import * as React from 'react';
import { useAppSelector } from '../../../app/hooks';
import { League, Player, PlayerPositionEnum, Team } from '../../../sdk/model'
import { toSalary, getSalaryForSeason } from '../../util/SalaryDisplay';
import { MaterialReactTable } from 'material-react-table';
import TeamDisplay from '../team/TeamDisplay'
import { Box, Typography, Grid, Card, CardContent, Divider, Chip, FormControl, RadioGroup, FormControlLabel, Radio } from '@mui/material';
import { ExpandLess as ExpandLessIcon, ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { useTheme, useMediaQuery } from '@mui/material';

interface teamContractReportProps {
  league : League,
  playerMap : Map<string | undefined, Player | undefined> 
}

const FUTURE_SEASONS_TO_DISPLAY = 4;

// Helper function to count players by position and rank
const countPlayersByPositionAndRank = (team: Team, playerMap: Map<string | undefined, Player | undefined>) => {
  const positionRanks: { [key: string]: { [rank: string]: number } } = {
    [PlayerPositionEnum.Qb]: { 'top5': 0, 'top12': 0, 'top24': 0, 'dartThrow': 0 },
    [PlayerPositionEnum.Rb]: { 'top5': 0, 'top12': 0, 'top24': 0, 'dartThrow': 0 },
    [PlayerPositionEnum.Wr]: { 'top5': 0, 'top12': 0, 'top24': 0, 'dartThrow': 0 },
    [PlayerPositionEnum.Te]: { 'top5': 0, 'top12': 0, 'top24': 0, 'dartThrow': 0 },
  };

  if (!team.playerRoster) return positionRanks;

  // Flatten all players from all roster positions
  const allPlayers = Object.values(team.playerRoster).flat().filter(Boolean);
  
  // Count players by position and rank
  allPlayers.forEach(playerInfo => {
    if (!playerInfo?.playerId) return;
    
    const player = playerMap.get(playerInfo.playerId);
    if (!player || !player.position || !positionRanks[player.position]) return;
    
    // Get the player's rank
    const rank = player?.quick_stats?.positionalRankOnYear || 99; // Default to 99 if rank is not available
    
    // Categorize by rank
    if (rank <= 5) {
      positionRanks[player.position]['top5']++;
    } else if (rank <= 12) {
      positionRanks[player.position]['top12']++;
    } else if (rank <= 24) {
      positionRanks[player.position]['top24']++;
    } else {
      positionRanks[player.position]['dartThrow']++;
    }
  });

  return positionRanks;
};

function TeamContractReport(props: teamContractReportProps) {
  const user = useAppSelector((state) => state.user);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [expanded, setExpanded] = React.useState<Record<string, boolean>>({});
  const [view, setView] = React.useState("payrollSalaries");
  
  const handleExpandAll = () => {
    if (Object.keys(expanded).length === props.league.teams!.length) {
      // If all are expanded, collapse all
      setExpanded({});
    } else {
      // Otherwise expand all
      const allExpanded = props.league.teams!.reduce((acc, row) => ({
        ...acc,
        [row.id!]: true
      }), {});
      setExpanded(allExpanded);
    }
  };

  const isAllExpanded = Object.keys(expanded).length > 0 && 
                       Object.values(expanded).every(Boolean);

  const columns = React.useMemo(
    () => {
      const baseColumns = [
        {
          accessorKey: 'name',
          header: 'Team Name',
          size: isMobile ? 50 : 65,
          Cell: ({ row }) => {
            const myTeam = row.original.owners!.some(owner => owner.id === user.id);
            return <TeamDisplay leagueId={props.league.id!} team={row.original} emphasized={myTeam} />
          }
        },
        {
          accessorKey: 'capSpace',
          header: props.league.currentSeasonYear + " Cap Space",
          minSize: 15,
          size: isMobile ? 25 : 15,
          Cell: ({ renderedCellValue }) => toSalary(renderedCellValue)
        }
      ];

      // Add additional columns only if not on mobile
      if (!isMobile) {
        if (view === "payrollSalaries") {
          baseColumns.push(
            {
              accessorKey: 'currentSeasonSalary',
              id: 'currentSeasonSalary',
              header: props.league.currentSeasonYear + " Salary",
              minSize: 15,
              size: 15,
              Cell: ({ renderedCellValue }) => toSalary(renderedCellValue)
            },
            {
              id: 'futureSalaries',
              header: 'Future Season Salaries',
              columns: [...Array(FUTURE_SEASONS_TO_DISPLAY).keys()].map(index => {
                const seasonYear = props.league.currentSeasonYear! + index + 1;
                return {
                  accessorKey: 'salariesBySeason.' + seasonYear,
                  header: seasonYear,
                  minSize: 15,
                  size: 15,
                  Cell: ({ renderedCellValue }) => {
                    return toSalary(renderedCellValue);
                  }
                }
              })
            }
          );
        } else if (view === "guaranteedSalaries") {
          baseColumns.push(
            {
              id: 'guaranteedSalaries',
              header: 'Guaranteed Salaries by Season',
              columns: [...Array(FUTURE_SEASONS_TO_DISPLAY + 1).keys()].map(index => {
                const seasonYear = props.league.currentSeasonYear! + index;
                return {
                  accessorKey: 'guaranteedPaymentsBySeason.' + seasonYear,
                  header: seasonYear,
                  minSize: 15,
                  size: 15,
                  Cell: ({ renderedCellValue }) => {
                    return toSalary(renderedCellValue);
                  }
                }
              })
            }
          );
        } else {
          baseColumns.push(
            {
              id: 'paidSalaries',
              header: 'Paid / Dead Cap by Season',
              columns: [...Array(FUTURE_SEASONS_TO_DISPLAY + 1).keys()].map(index => {
                const seasonYear = props.league.currentSeasonYear! + index;
                return {
                  accessorKey: 'clearedPaymentsBySeason.' + seasonYear,
                  header: seasonYear,
                  minSize: 15,
                  size: 15,
                  Cell: ({ renderedCellValue }) => {
                    return toSalary(renderedCellValue);
                  }
                }
              })
            }
          );
        }
      }

      return baseColumns;
    },
    [isMobile, props.league.id, props.league.currentSeasonYear, user.id, view]
  );

  const renderDetailPanel = React.useCallback(
    ({ row }) => {
      const positionRanks = row.original.positionRanks || {};
      const positions = Object.values(PlayerPositionEnum);
      
      if (!row.original) {
        return (
          <Box sx={{ p: 2, backgroundColor: 'background.paper' }}>
            <Typography variant="body2" color="error">Error: Team data not available</Typography>
          </Box>
        );
      }

      return (
        <Box sx={{ p: 3, backgroundColor: 'background.paper' }}>
          <Typography variant="h6" gutterBottom sx={{ mb: 3, fontWeight: 'bold', color: 'primary.main' }}>
            Positional Depth Chart
          </Typography>
          
          <Grid container spacing={2}>
            {positions.map((position) => {
              const ranks = positionRanks[position] || { top5: 0, top12: 0, top24: 0, dartThrow: 0 };
              const total = Object.values(ranks).reduce((sum, count) => sum + count, 0);
              
              if (total === 0) return null;
              
              return (
                <Grid item xs={12} sm={6} md={3} key={position} sx={{ display: 'flex' }}>
                  <Card variant="outlined" sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
                    <CardContent sx={{ flexGrow: 1, p: 2 }}>
                      <Typography 
                        variant="subtitle1" 
                        sx={{ 
                          fontWeight: 'bold', 
                          color: 'text.secondary',
                          mb: 1.5,
                          textAlign: 'center',
                          fontSize: '1.1rem'
                        }}
                      >
                        {position}
                      </Typography>
                      
                      <Box sx={{ 
                        display: 'grid', 
                        gridTemplateColumns: '1fr 1fr',
                        gap: 1,
                        mb: 1.5
                      }}>
                        {[
                          { label: 'Top 5', value: ranks.top5, color: 'success.main' },
                          { label: '6-12', value: ranks.top12, color: 'info.main' },
                          { label: '13-24', value: ranks.top24, color: 'warning.main' },
                          { label: '24+', value: ranks.dartThrow, color: 'text.secondary' },
                        ].map(({ label, value, color }) => {
                          const isZero = value === 0;
                          const textColor = isZero ? 'text.disabled' : color;
                          const chipBgColor = isZero ? 'action.disabledBackground' : `${color}22`;
                          const chipTextColor = isZero ? 'text.disabled' : color;
                          
                          return (
                            <React.Fragment key={label}>
                              <Typography 
                                variant="body2" 
                                color={textColor}
                                sx={{ 
                                  display: 'flex', 
                                  alignItems: 'center',
                                  justifyContent: 'flex-end',
                                  pr: 1,
                                  minHeight: '24px',
                                  opacity: isZero ? 0.6 : 1
                                }}
                              >
                                {label}:
                              </Typography>
                              <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                                <Chip 
                                  label={value} 
                                  size="small" 
                                  sx={{ 
                                    minWidth: '24px', 
                                    height: '24px', 
                                    borderRadius: '12px',
                                    backgroundColor: chipBgColor,
                                    color: chipTextColor,
                                    fontWeight: 'bold',
                                    justifyContent: 'center',
                                    opacity: isZero ? 0.6 : 1
                                  }} 
                                />
                              </Box>
                            </React.Fragment>
                          );
                        })}
                      </Box>
                      
                      <Divider sx={{ my: 1 }} />
                      <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        mt: 1,
                        px: 1
                      }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>Total:</Typography>
                        <Chip 
                          label={total} 
                          color="primary" 
                          size="small"
                          sx={{ 
                            fontWeight: 'bold',
                            minWidth: '32px',
                            justifyContent: 'center'
                          }}
                        />
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      );
    },
    [expanded]
  );

  const data = React.useMemo(() => {
    if (!props.league.teams) return [];
    
    return props.league.teams.map(team => {
      const capSpace = props.league.currentSeason!.nflSeason!.salaryCap! - team.currentSeasonSalary!;
      const contractMap = new Map(Object.values(team.playerRoster!).flat().filter(player => player).map(player => [player.contract?.id, player.contract]));
      const salariesBySeason = [...Array(FUTURE_SEASONS_TO_DISPLAY).keys()].reduce((builder, index) => {
        const newValue = {...builder};
        const seasonYear = props.league.currentSeasonYear! + index + 1;
        const salaryForSeason = getSalaryForSeason(team.paymentsBySeason![seasonYear]);
        newValue[seasonYear.toString()] = salaryForSeason;
        return newValue;
      }, {});
      const guaranteedPaymentsBySeason = Object.keys(team.paymentsBySeason!).reduce((builder, seasonYear) => {
        const newValue = {...builder};
        const guaranteedPayments = team.paymentsBySeason![seasonYear]
          .reduce((seasonSum, paymentSeason) => {
            const contract = contractMap.get(paymentSeason.contractId);
            const contractWeek = contract?.seasons?.find(season => season.season_year === Number(seasonYear))?.weeks![0]!;
            const nonGuaranteed = contractWeek ? contractWeek.fullSalary! - contractWeek.guaranteedSalary! : 0;
            return seasonSum + paymentSeason.payments!
              .reduce((paymentSum, payment) => paymentSum + (payment!.completed ? payment.price! : Math.max(payment.price! - nonGuaranteed, 0)), 0)
            }, 0);
        newValue[seasonYear] = guaranteedPayments;
        return newValue;
      }, {});
      const clearedPaymentsBySeason = Object.keys(team.paymentsBySeason!).reduce((builder, seasonYear) => {
        const newValue = {...builder};
        const clearedPayments = team.paymentsBySeason![seasonYear]
          .map(paymentSeason => paymentSeason.payments)
          .reduce((seasonSum, payments) => seasonSum + payments
              .filter(payment => payment.completed)
              .reduce((paymentSum, payment) => paymentSum + payment.price, 0), 0);
        newValue[seasonYear] = clearedPayments;
        return newValue;
      }, {});
      
      const positionRanks = countPlayersByPositionAndRank(team, props.playerMap);
      
      return {
        ...team,
        capSpace,
        salariesBySeason,
        positionRanks,
        guaranteedPaymentsBySeason,
        clearedPaymentsBySeason
      };
    });
  }, [props.league.teams, props.league.currentSeason, props.league.currentSeasonYear, props.playerMap]);

  return (
    <Box>
      <FormControl>
        <RadioGroup
            name="radio-buttons-group"
            value={view}
            onChange={(event) => setView(event.target.value)}
            sx={{display: 'inline-block'}}>
          <FormControlLabel value="payrollSalaries" control={<Radio />} label="Salaries on Payroll" />
          <FormControlLabel value="guaranteedSalaries" control={<Radio />} label="Guaranteed Salaries" />
          <FormControlLabel value="paidSalaries" control={<Radio />} label="Paid Salaries / Dead Cap" />
        </RadioGroup>
      </FormControl>
      <MaterialReactTable
        columns={columns}
        data={data}
        enableExpanding
        enableExpandAll={true}
        enablePagination={false}
        renderDetailPanel={renderDetailPanel}
        getRowId={(row) => row.id!}
        initialState={{
          sorting: [
            {
              id: 'capSpace',
              desc: false,
            },
          ],
        }}
        displayColumnDefOptions={{
          'mrt-row-expand': {
            size: 5, // Make the expand column smaller
            muiTableHeadCellProps: {
              align: 'center',
            },
            muiTableBodyCellProps: {
              align: 'center',
            },
          },
        }}
        muiTablePaperProps={{
          sx: {
            backgroundColor: 'background.paper',
            overflow: 'hidden',
          },
        }}
        muiTableContainerProps={{
          sx: {
            maxHeight: 'calc(100vh - 200px)',
          },
        }}
        muiTableHeadCellProps={{
          sx: {
            fontSize: isMobile ? '0.75rem' : '0.875rem',
            padding: isMobile ? '8px 4px' : '16px',
          },
        }}
        muiTableBodyCellProps={{
          sx: {
            fontSize: isMobile ? '0.75rem' : '0.875rem',
            padding: isMobile ? '8px 4px' : '16px',
          },
        }}
      />
    </Box>
  );
}

export default TeamContractReport;